import React, { useState } from 'react';
import { Card, Tag, Button, Upload, message, Alert, Space } from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  FilePdfOutlined,
  EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { UPLOAD_STUDENT_DOCUMENT_URL } from '../../../../Constants/Api/azure/azure';
import { saveStudentDocument } from '../../../../Constants/Api/functions/function';

const DOCUMENTS_CONFIG = [
  {
    key: 'nic_front',
    title: 'NIC – Front Side',
    required: true,
    description: 'Clear photograph or scan of the front side of your Sri Lankan National Identity Card.',
  },
  {
    key: 'nic_back',
    title: 'NIC – Back Side',
    required: true,
    description: 'Clear photograph or scan of the back side of your Sri Lankan National Identity Card.',
  },
  {
    key: 'passport_photo',
    title: 'Passport-Size Photograph',
    required: true,
    description: 'Formal white/light background passport-sized portrait for student ID and records.',
  },
  {
    key: 'passport',
    title: 'Passport (Bio Page)',
    required: false,
    description: 'Required for international students or students without a Sri Lankan National ID.',
  },
  {
    key: 'educational_cert',
    title: 'Highest Educational Certificate',
    required: false,
    description: 'Degree certificate, A/L certificate, or transcript relevant to course admission.',
  },
  {
    key: 'professional_cert',
    title: 'Professional Qualification Certificate',
    required: false,
    description: 'CIMA, ACCA, CA, CIM, AAT, or equivalent professional membership/exam certificate.',
  },
  {
    key: 'other',
    title: 'Other Supporting Document',
    required: false,
    description: 'Any additional work experience letters, recommendation letters, or diplomas.',
  }
];

const Section08Documents = ({ userId, documents = [], onDocumentUploaded }) => {
  const [uploadingKey, setUploadingKey] = useState(null);

  const getDocStatus = (docType) => {
    return documents.find((d) => d.documentType === docType) || null;
  };

  const handleUpload = async (docConfig, { file, onSuccess, onError }) => {
    try {
      setUploadingKey(docConfig.key);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', `${userId}_${docConfig.key}`);

      const response = await fetch(UPLOAD_STUDENT_DOCUMENT_URL, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        let errMsg = `Upload failed (${response.status})`;
        try {
          const errData = await response.json();
          if (errData?.message) errMsg = errData.message;
        } catch (_) {}
        throw new Error(errMsg);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'File upload failed');
      }

      // Record in student_documents table
      const docRecord = {
        userId,
        documentType: docConfig.key,
        fileName: data.originalname || file.name,
        fileUrl: data.url,
        fileType: data.mimetype || file.type,
        fileSize: data.size || file.size,
      };

      const saveRes = await saveStudentDocument(docRecord);
      if (saveRes && saveRes.success) {
        message.success(`${docConfig.title} uploaded successfully!`);
        if (onDocumentUploaded) {
          onDocumentUploaded();
        }
        onSuccess(data, file);
      } else {
        throw new Error(saveRes?.message || 'Failed to record document');
      }
    } catch (err) {
      console.error('Document upload error:', err);
      message.error(err.message || 'Upload failed');
      onError(err);
    } finally {
      setUploadingKey(null);
    }
  };

  const renderStatusTag = (docRecord) => {
    if (!docRecord) {
      return <Tag color="default">Not Uploaded</Tag>;
    }

    switch (docRecord.status) {
      case 'verified':
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Verified
          </Tag>
        );
      case 'rejected':
        return (
          <Tag color="error" icon={<CloseCircleOutlined />}>
            Rejected
          </Tag>
        );
      case 'under_review':
        return (
          <Tag color="warning" icon={<ClockCircleOutlined />}>
            Under Review
          </Tag>
        );
      case 'submitted':
      default:
        return (
          <Tag color="processing" icon={<ClockCircleOutlined />}>
            Submitted
          </Tag>
        );
    }
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <span className="section-badge">SECTION 08</span>
        <h3>Verification Documents</h3>
        <p className="section-description">
          Please upload genuine, high-resolution scans or photos of your identification and certificates. Accepted formats: <strong>JPG, PNG, PDF</strong> (Maximum 10MB per document).
        </p>
      </div>

      <div className="documents-grid">
        {DOCUMENTS_CONFIG.map((docConfig) => {
          const docRecord = getDocStatus(docConfig.key);
          const isUploading = uploadingKey === docConfig.key;

          return (
            <Card key={docConfig.key} className="doc-upload-card" bordered={false}>
              <div className="doc-card-top">
                <div className="doc-info">
                  <div className="doc-title-row">
                    <h4 className="doc-title">{docConfig.title}</h4>
                    {docConfig.required && <span className="doc-required-badge">Required</span>}
                  </div>
                  <p className="doc-desc">{docConfig.description}</p>
                </div>
                <div className="doc-status-col">{renderStatusTag(docRecord)}</div>
              </div>

              {/* Show Rejection Reason if Rejected */}
              {docRecord && docRecord.status === 'rejected' && (
                <Alert
                  type="error"
                  showIcon
                  message="Document Rejected"
                  description={
                    <div>
                      <span><strong>Reason:</strong> {docRecord.rejectionReason || 'Document is illegible or does not match submitted name.'}</span>
                      <div style={{ marginTop: 4 }}>Please upload a clear, revised document below.</div>
                    </div>
                  }
                  style={{ marginBottom: 12, borderRadius: 8 }}
                />
              )}

              {/* Document Details & Actions */}
              <div className="doc-card-bottom">
                {docRecord ? (
                  <div className="uploaded-file-row">
                    <div className="file-info-preview">
                      <FilePdfOutlined style={{ fontSize: 20, color: '#ef4444', marginRight: 8 }} />
                      <span className="file-name-text" title={docRecord.fileName}>
                        {docRecord.fileName}
                      </span>
                    </div>

                    <Space>
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        href={docRecord.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </Button>

                      <Upload
                        accept="image/png,image/jpeg,image/webp,application/pdf"
                        customRequest={(options) => handleUpload(docConfig, options)}
                        showUploadList={false}
                        disabled={isUploading}
                      >
                        <Button
                          size="small"
                          type={docRecord.status === 'rejected' ? 'primary' : 'default'}
                          danger={docRecord.status === 'rejected'}
                          icon={<ReloadOutlined />}
                          loading={isUploading}
                        >
                          {docRecord.status === 'rejected' ? 'Re-upload' : 'Replace'}
                        </Button>
                      </Upload>
                    </Space>
                  </div>
                ) : (
                  <div className="upload-btn-container">
                    <Upload
                      accept="image/png,image/jpeg,image/webp,application/pdf"
                      customRequest={(options) => handleUpload(docConfig, options)}
                      showUploadList={false}
                      disabled={isUploading}
                    >
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        loading={isUploading}
                        className="btn-upload-doc"
                      >
                        {isUploading ? 'Uploading...' : `Upload ${docConfig.title}`}
                      </Button>
                    </Upload>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Section08Documents;
